import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { getCoafGroupLabel } from '../../services/complianceTranslations';

const COAF_GROUP_ORDER = [
  'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
  'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'CIRCULAR_3978',
];

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const getRuleStatusLabel = (rule) => {
  if (typeof rule?.active === 'boolean') {
    return rule.active ? 'Ativa' : 'Inativa';
  }

  const status = String(rule?.status || '').trim();
  return status || '-';
};

export default function RulesSelectionPreview({
  title = 'Mapa de regras',
  subtitle = '',
  rules = [],
  loading = false,
  errorMessage = '',
  emptyMessage = 'Nenhuma regra encontrada.',
  highlightedGroups = [],
}) {
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredRules = React.useMemo(() => {
    const normalizedSearch = normalizeText(searchTerm);
    const hasHighlightedGroups = Array.isArray(highlightedGroups) && highlightedGroups.length > 0;

    return rules.filter((rule) => {
      const inHighlightedGroup = !hasHighlightedGroups || highlightedGroups.includes(rule.coafGroup || 'Outros');
      if (!inHighlightedGroup) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const code = normalizeText(rule.code);
      const titleText = normalizeText(rule.title);
      const group = normalizeText(rule.coafGroup);

      return code.includes(normalizedSearch) || titleText.includes(normalizedSearch) || group.includes(normalizedSearch);
    });
  }, [highlightedGroups, rules, searchTerm]);

  const rulesByGroup = React.useMemo(() => {
    return filteredRules.reduce((groups, rule) => {
      const groupKey = rule.coafGroup || 'Outros';
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(rule);
      return groups;
    }, {});
  }, [filteredRules]);

  const sortedGroupKeys = React.useMemo(() => {
    return Object.keys(rulesByGroup).sort((a, b) => {
      const left = COAF_GROUP_ORDER.indexOf(a);
      const right = COAF_GROUP_ORDER.indexOf(b);
      return (left === -1 ? 999 : left) - (right === -1 ? 999 : right);
    });
  }, [rulesByGroup]);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography component="h2" variant="h6">{title}</Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {subtitle}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Chip size="small" label={`Total: ${filteredRules.length}`} />
              <Chip size="small" variant="outlined" label={`Grupos: ${sortedGroupKeys.length}`} />
            </Stack>
          </Stack>

          <TextField
            label="Buscar regra"
            size="small"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Código, título ou grupo"
            disabled={loading}
          />

          {errorMessage ? <Alert severity="warning">{errorMessage}</Alert> : null}

          {loading ? (
            <Stack sx={{ alignItems: 'center', py: 4 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5 }}>
                Carregando regras...
              </Typography>
            </Stack>
          ) : null}

          {!loading && !filteredRules.length ? (
            <Alert severity="info">{emptyMessage}</Alert>
          ) : null}

          {!loading ? sortedGroupKeys.map((group) => (
            <Accordion key={group} variant="outlined" disableGutters>
              <AccordionSummary>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', py: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                    {getCoafGroupLabel(group)}
                  </Typography>
                  <Chip size="small" label={`${rulesByGroup[group].length} regras`} />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small" aria-label={`preview de regras do grupo ${group}`}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Título</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rulesByGroup[group].map((rule) => (
                        <TableRow key={rule.id || `${group}-${rule.code}-${rule.title}`} hover>
                          <TableCell>{rule.code || '-'}</TableCell>
                          <TableCell>{rule.title || '-'}</TableCell>
                          <TableCell>{getRuleStatusLabel(rule)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}
